import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { featureContent } from './Peculiarities.content';
import styles from './Peculiarities.module.sass';
import cloud1 from './assets/cloud-1.webp';
import cloud3 from './assets/cloud-3.webp';
import cloud4 from './assets/cloud-4.webp';

gsap.registerPlugin(ScrollTrigger);

export function Peculiarities() {
  const { title, subtitle, cards, area } = featureContent;
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);
  const areaHeaderRef = useRef(null);
  const areaTextRef = useRef(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const cardsContainer = cardsRef.current;
    const areaHeader = areaHeaderRef.current;
    const areaText = areaTextRef.current;

    if (!section || !cardsContainer) return;

    let areaRevealDone = false;

    const tryRevealArea = (self) => {
      if (areaRevealDone || !areaHeader || !areaText) return;
      if (self.progress > 0.99) {
        areaRevealDone = true;
        gsap.to([areaHeader, areaText], {
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.85,
          ease: 'power2.out',
          stagger: 0.12,
        });
      }
    };

    const ctx = gsap.context(() => {
      const startOffset = window.innerHeight <= 800 ? 'top top-=250px' : window.innerHeight <= 900 ? 'top top-=180px' : 'top top-=100px';

      ScrollTrigger.create({
        trigger: section,
        pin: true,
        start: startOffset,
        end: () => `+=${cardsContainer.scrollWidth - window.innerWidth}`,
        scrub: 1,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        refreshPriority: -10,
        animation: gsap.to(cardsContainer, {
          x: () => -(cardsContainer.scrollWidth - window.innerWidth),
          ease: 'none',
        }),
        onUpdate(self) {
          tryRevealArea(self);
        },
        onRefresh(self) {
          tryRevealArea(self);
        },
      });
    }, section);

    const refresh = () => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    };

    const rafId = requestAnimationFrame(refresh);
    window.addEventListener('load', refresh);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('load', refresh);
      ctx.revert();
    };
  }, []);

  return (
    <section
      id="features"
      className={styles.peculiarities}
      aria-labelledby="peculiarities-heading"
      ref={sectionRef}
    >
      <div className={styles.peculiarities__bg} aria-hidden />
      <div className={styles.peculiarities__bg2} aria-hidden />
      <div className={styles.peculiarities__clouds} aria-hidden>
        <img src={cloud1} alt="" className={styles['peculiarities__cloud--1']} />
        <img src={cloud3} alt="" className={styles['peculiarities__cloud--3']} />
        <img src={cloud4} alt="" className={styles['peculiarities__cloud--4']} />
      </div>

      <div className={styles.peculiarities__inner}>
        <header className={styles.peculiarities__header}>
          <p className={styles.peculiarities__kicker}>{title}</p>
          <h2 id="peculiarities-heading" className={styles.peculiarities__title}>
            {subtitle.map((seg, i) => (
              <span key={i} className={styles.peculiarities__titleLine}>
                <span
                  className={seg.accent ? styles['peculiarities__titleLine--accent'] : undefined}
                >
                  {seg.text}
                </span>
              </span>
            ))}
          </h2>
        </header>
        <div className={styles.peculiarities__cards} ref={cardsRef}>
          {cards.map((card, i) => (
            <article
              key={i}
              className={`${styles.peculiarities__card} ${styles[`peculiarities__card--${i + 1}`]}`}
            >
              <div className={styles.peculiarities__cardImageWrap}>
                <img
                  src={card.image}
                  alt=""
                  className={styles.peculiarities__cardImage}
                  loading="lazy"
                />
              </div>
              <div className={styles.peculiarities__cardBody}>
                <p className={styles.peculiarities__cardTitle}>
                  {card.title.map((seg, j) => (
                    <span key={j} className={styles.peculiarities__cardTitleLine}>
                      <span
                        className={
                          seg.accent ? styles['peculiarities__cardTitleLine--accent'] : undefined
                        }
                      >
                        {seg.text}
                      </span>
                    </span>
                  ))}
                </p>
                <p
                  className={`${styles.peculiarities__cardText} ${card.is_large_text ? styles['peculiarities__cardText--large'] : ''}`}
                >
                  {card.text.map((seg, j) => (
                    <span key={j}>
                      <span
                        className={
                          seg.accent ? styles['peculiarities__cardText--accent'] : undefined
                        }
                      >
                        <span dangerouslySetInnerHTML={{ __html: seg.text }} />
                      </span>
                      {j < card.text.length - 1 ? ' ' : ''}
                    </span>
                  ))}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className={styles.peculiarities__area}>
        <header className={styles.peculiarities__areaHeader} ref={areaHeaderRef}>
          <div className={styles.peculiarities__areaTitle}>
            {area.title.map((seg, i) => (
              <div key={i} className={styles['peculiarities__area-TitleLine']}>
                <span
                  className={
                    seg.accent ? styles['peculiarities__area-TitleLine--accent'] : undefined
                  }
                >
                  {seg.text}
                </span>
              </div>
            ))}
          </div>
        </header>
        <div className={styles.peculiarities__areaText} ref={areaTextRef}>
          {area.text.map((seg, i) => (
            <span key={i} className={styles.peculiarities__areaTextLine}>
              <span
                className={seg.accent ? styles['peculiarities__areaTextLine--accent'] : undefined}
              >
                {seg.text}
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
